import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { AdSlot } from '../components/AdSlot';
import { perDay, useSite } from '../site/SiteProvider';

/**
 * ヘルプ（`/help`）。よくある質問に答える静的なページ。
 *
 * 書き方の方針は診断の文面と同じで、断定せず、できないことはできないと書く。
 * 測定の限界（doc/ASSESSMENT.md）とプライバシー（doc/AUTH.md）は、ここで正直に説明する。
 *
 * **正は `/terms` と `/privacy`、ここは要約**（doc/AUTH.md §13）。二重に書くと必ずずれるので、
 * 保存しているものや広告の配信事業者の詳細は書かず、ポリシーへのリンクで済ませる。
 */
interface Item {
  readonly id: string;
  readonly q: string;
  readonly a: React.ReactNode;
}

/** 1 人あたりの回数（管理メニューの「上限」で変わるので、固定で書かずにサーバの値を出す）。 */
function ImageLimit() {
  return <>{perDay(useSite().limits.userImageDailyLimit)}</>;
}

function CompatLimit() {
  return <>{perDay(useSite().limits.userCompatDailyLimit ?? 3)}</>;
}

function CompatSceneLimit() {
  const { limits } = useSite();
  return <>{perDay(limits.userCompatImageDailyLimit === undefined ? 3 : limits.userCompatImageDailyLimit)}</>;
}

const SECTIONS: readonly { readonly title: string; readonly items: readonly Item[] }[] = [
  {
    title: 'はじめに',
    items: [
      {
        id: 'what',
        q: 'GenDan は何ができるサイトですか',
        a: (
          <>
            <p>ふたつあります。</p>
            <p>ひとつは、<strong>いくつかの問いに答えて、自分に近いキャラクターを知る</strong>ことです。「日本むかし話」なら桃太郎や浦島太郎、「深海生物」ならダイオウイカやチョウチンアンコウ、というふうに出ます。結果はリンクで友達に送れて、そのまま SNS にも流せます。</p>
            <p>もうひとつは、<strong>テーマを一文書いて、診断そのものを作る</strong>ことです。キャラクターは Gemini が考えます。作った診断は他の人も受けられます。</p>
          </>
        ),
      },
      {
        id: 'ads',
        q: '広告は出ますか',
        a: (
          <>
            <p><strong>いまは広告を出していません。</strong>GenDan は無料で使えますが、診断やキャラクターの絵を作るたびに費用がかかるため、運営を続けるために、今後<strong>広告（アフィリエイト広告を含みます）</strong>を載せることがあります。</p>
            <p>載せるときは、枠に「広告」「PR」と書き、設問に答えている途中の画面には出しません。広告から商品やサービスを申し込むと、運営に紹介料が入ることがあります。診断の回答や結果を広告の事業者に渡すことはありません（<Link to="/privacy">プライバシーポリシー</Link>の 5.）。</p>
          </>
        ),
      },
      {
        id: 'login-needed',
        q: 'ログインは必要ですか',
        a: (
          <>
            <p>診断を受けるのも、診断を作るのも、ログインなしでできます。</p>
            <p>ログインが要るのは、作った診断に<strong>キャラクターの絵を付ける</strong>とき、<strong>自分の診断を管理する</strong>とき、<strong>相性の詳細版</strong>（相手の結果と比べる・AI の読み・その PDF）を使うとき、<strong>お問い合わせ</strong>や<strong>相性ドラマの動画のリクエスト</strong>を送るときです。</p>
          </>
        ),
      },
      {
        id: 'time',
        q: 'どのくらい時間がかかりますか',
        a: <p>診断は、短い版が 24 問で 3 分ほど、いちばん長い版が 72 問で 10 分ほどです。別の測り方の「ビッグファイブ」は 60 問で 8 分ほどです。診断を作るほうは、キャラクターを考えるのに 30 秒〜1 分ほどかかります。絵はそのあと、裏で描かれます。</p>,
      },
    ],
  },
  {
    title: '結果について',
    items: [
      {
        id: 'accuracy',
        q: '結果はどのくらい当たりますか',
        a: (
          <>
            <p>当たる・当たらないというより、<strong>答えた内容から見える傾向</strong>とお考えください。同じ人でも、体調や状況で答えが変わります。</p>
            <p>結果には軸ごとの「確からしさ」を出しています。短い版は測る側面が少ないので、そのぶん幅があります。<strong>採用や選抜の判断に使えるだけの精度は確かめられていません。</strong>遊びとして、自分を言葉にするきっかけとしてお使いください。</p>
          </>
        ),
      },
      {
        id: 'jung',
        q: '「ユングの類型でいえば主機能は〜型」とは何ですか',
        a: (
          <>
            <p>結果ページの「どう測ったか」に出る一文です。4 文字の型を、スイスの精神科医ユングが 1921 年に示した「態度（外向・内向）× 機能（思考・感情・感覚・直観）」の分け方で読み替えたものです。主機能はその人がいちばん自然に使う心の働き、補助機能はそれを支えるもうひとつの働きを指します。</p>
            <p>読み方の手順、8 つの型の説明、16 型の対応表は<Link to="/help/jung">ユングのタイプ論と、この診断の型の読み方</Link>にまとめました。</p>
          </>
        ),
      },
      {
        id: 'bigfive',
        q: '「ビッグファイブ」とは何ですか。他の 3 つと何が違いますか',
        a: (
          <>
            <p>「さっと」「ふつう」「じっくり」は同じ設問の長さ違いで、6 本の軸から型とキャラクターを出します。「ビッグファイブ」は<strong>別の質問紙（60 問）</strong>で、性格心理学でいちばん研究の裏付けが多い <strong>5 つの因子</strong>（外向性・協調性・誠実性・神経症傾向・開放性）と、その 15 の側面を 0〜100 のスコアで出します。</p>
            <p>ビッグファイブは型ではなく連続した傾向なので、結果ページの「どう測ったか」には 5 因子のスコアと測定の誤差の帯が出ます。キャラクターと型は、5 因子を 6 本の軸に写して<strong>目安として</strong>添えています。写し方の根拠と限界は<Link to="/help/bigfive">ビッグファイブについて</Link>にまとめました。</p>
          </>
        ),
      },
      {
        id: 'again',
        q: 'もう一度受けたら結果が変わりました',
        a: <p>よくあることです。特に、ある軸で真ん中に近かった場合は、少しの違いで反対側に出ます。結果ページに前回との比較が出るので、どの軸が動いたか見てみてください。</p>,
      },
      {
        id: 'compat',
        q: '相性はどう見ていますか',
        a: (
          <>
            <p>結果ページの「相性と付き合い方」で見られます。<strong>相性の良し悪しを点数で決めるものではありません</strong>。性格が似ているかどうかで関係の満足度が決まる割合は小さいと研究で報告されているので、すれ違いが起きやすいところと、その手当てを書いています。</p>
            <p><strong>型どうしの付き合い方</strong>は、相手の型（4 文字）を選ぶと出る一般論です。ログインは要りません。型の文字だけで決め打ちした文なので、PDF には載せていません。</p>
            <p><strong>詳細版</strong>は、相手の結果ページの URL を貼って、2 人の 18 の側面を比べます（ログインが必要です）。差が測定の誤差を超えるところ（25 以上）を「はっきり違う」とし、そろって同じ側に寄るところと分けて、側面ごとの手当てを出します。PDF でダウンロードできます。</p>
            <p>詳細版では、相手との関係（恋人・家族・上司・部下など）を選ぶと、<strong>AI（Google Gemini）が関係に合わせた付き合い方</strong>も書きます。1 人あたりの回数を決めています（いまは <CompatLimit />）。一度書いた組み合わせは保存しておき、開き直しても回数は数えません。AI に送るのは側面の名前とスコア、選んだ関係だけで、回答そのものや氏名は送りません。AI の文章は誤りを含むことがあります。</p>
            <p>AI の読みのあとに「エピソードの絵を描く」を押すと、読みの 3 つの場面を<strong>2 人のキャラクターの絵</strong>にします（1 回で 3 枚。いまは <CompatSceneLimit />、AI の読みとは別に数えます）。描いた絵は保存しておき、開き直しても回数は数えません。絵に描くのはキャラクターだけで、回答や氏名は使いません。AI が描いた想像の場面です。</p>
            <p>結果ページは URL を知っている人なら誰でも開けるので、比べる前に相手に一声かけてください。</p>
          </>
        ),
      },
      {
        id: 'share',
        q: '結果は誰かに見られますか',
        a: <p>結果ページは<strong>URL を知っている人だけ</strong>が見られます。検索には出ません。氏名やメールアドレスは結果に入っていないので、URL を伝えない限り、誰の結果か分かりません。</p>,
      },
    ],
  },
  {
    title: '診断を作る・キャラクターの絵',
    items: [
      {
        id: 'images',
        q: 'キャラクターの絵が付きません',
        a: (
          <>
            <p>絵が付くのは、<strong>ログインして作った診断だけ</strong>です。絵は 1 人 1 枚ずつ生成していて費用がかかるため、<strong>1 人あたりの回数を決めています（いまは <ImageLimit />）</strong>。数えるのは「生成を始めた回数」で、1 回で登場人物の人数ぶん描きます。</p>
            <p>上限に達した日でも診断そのものは作れます。絵は日付が変わってから、<Link to="/me">自分のページ</Link>の「作った診断」で描けます。</p>
            <p>ログインせずに作った診断でも、<strong>作ったブラウザなら</strong>あとからログインして絵を付けられます。</p>
          </>
        ),
      },
      {
        id: 'delete',
        q: '作った診断を消したい',
        a: (
          <>
            <p><Link to="/me">自分のページ</Link>の「作った診断」か、その診断の入口ページから消せます。</p>
            <p>消すと一覧と入口から消えます。<strong>すでに出ている結果ページは残ります</strong>。受けた人の結果まで消してしまわないためです。戻したいときは <Link to="/contact">お問い合わせ</Link> からご依頼ください。</p>
            <p>ログインせずに作った診断は所有者がいないので、<strong>誰でも消せます</strong>。残しておきたい診断は、ログインしてから作ってください。</p>
          </>
        ),
      },
      {
        id: 'delete-result',
        q: '受けた診断の結果を消したい',
        a: (
          <>
            <p>ログインして受けた結果は、<Link to="/me">自分のページ</Link>の「受けた診断」から消せます。回答も一緒に消え、<strong>元には戻せません</strong>。シェアした結果ページも開けなくなり、その結果を使った相性の AI の読みも消えます。</p>
            <p>ログインせずに受けた結果は、<Link to="/contact">お問い合わせ</Link> から結果ページの URL を添えてご依頼ください。</p>
          </>
        ),
      },
      {
        id: 'delete-account',
        q: 'アカウントを削除したい',
        a: (
          <>
            <p><Link to="/me?tab=account">自分のページ</Link>の「アカウント」のいちばん下から削除できます。本人確認のため、もう一度ログインしていただきます。<strong>元には戻せません</strong>。</p>
            <p>ログインして受けた結果（回答ごと）・お問い合わせ・ログインの記録が消えます。作った診断は新しく受けられなくなり、ほかの人がすでに受けた結果ページは残ります。</p>
          </>
        ),
      },
      {
        id: 'bad-theme',
        q: '変なテーマの診断を見つけました',
        a: <p>入口や結果のページのいちばん下にある <strong>「この診断を通報する」</strong> からお知らせください（ログインは要りません。送った方のことは記録しません）。運営が確かめて、問題があれば消します。ログインせずに作られた診断なら、入口ページから誰でも消せます。</p>,
      },
    ],
  },
  {
    title: 'ログインとアカウント',
    items: [
      {
        id: 'methods',
        q: 'どのログイン方法が使えますか',
        a: <p>Google アカウントと、メールアドレス・パスワードでの登録が使えます。同じメールアドレスなら、あとから別の方法を足しても同じアカウントとして繋がります。どの方法でも、できることは同じです。</p>,
      },
      {
        id: 'reuse',
        q: '別の診断を受けるとき、また全部答えないといけませんか',
        a: (
          <>
            <p>設問はどの診断でも同じです（「さっと」「ふつう」「じっくり」は問いの数が違うだけで、短い版の問いは長い版に含まれています）。ログインして受けた診断があれば、別の診断の入口に「答えずに結果を見る」が出て、前の回答で結果が出ます。</p>
            <p>「ふつう」で受けたあとに「じっくり」を選んだときは、足りない 24 問だけ答えます。ログインせずに受けた分は回答が本人に結び付いていないので使えません。使い回した結果は、診断の信頼性を調べる集計からは除いています（同じ人の同じ回答を二重に数えないため）。</p>
            <p>「ビッグファイブ」は別の質問紙なので、3 つの深さとのあいだでは回答を使い回せません。ビッグファイブどうしなら使い回せます。</p>
          </>
        ),
      },
      {
        id: 'verify',
        q: '確認メールが届きません',
        a: <p>迷惑メールに入っていないか見てください。それでも無いときは、<Link to="/me">自分のページ</Link>から送り直せます。メールアドレスの確認は、パスワードを忘れたときの再設定に使います。</p>,
      },
      {
        id: 'stored',
        q: '何を保存していますか',
        a: (
          <>
            <p>ログインについて保存しているのは、利用者 ID と、絵を描いた回数・相性の AI の読みを頼んだ回数、権限、同意した版だけです。<strong>氏名は保存していません。</strong>表示名やメールアドレスは、ログインのたびに受け取って画面に出しているだけです。</p>
            <p>メールアドレスを保存するのは、お問い合わせで返信先を書いたときだけです。</p>
            <p>診断の結果には、回答内容と採点結果が入ります。信頼性を調べるために使いますが、個人を特定できる情報は入っていません。</p>
            <p>何をどのくらい持っているかの一覧は <Link to="/privacy">プライバシーポリシー</Link> にあります。</p>
          </>
        ),
      },
      {
        id: 'consent',
        q: '同意を求められました',
        a: (
          <>
            <p>診断を受けるのも作るのも、同意なしでできます。お願いしているのは、<strong>アカウントを作るとき</strong>だけです。</p>
            <p>いつ、どの版に同意したかを記録しています。本文を変えたときは、次のログインでもう一度お尋ねします。<Link to="/terms">利用規約</Link>と<Link to="/privacy">プライバシーポリシー</Link>はいつでも読めます。</p>
          </>
        ),
      },
    ],
  },
];

export function HelpPage() {
  const { user } = useAuth();
  return (
    <main className="page help">
      <h1 className="page__title">ヘルプ</h1>
      <p className="page__lead">よくある質問をまとめました。ここに無いことは、<Link to="/contact">お問い合わせ</Link>からお知らせください。</p>

      <nav className="help__toc" aria-label="目次">
        {SECTIONS.map((section) => (
          <a key={section.title} className="chip" href={`#${encodeURIComponent(section.title)}`}>{section.title}</a>
        ))}
      </nav>

      {SECTIONS.map((section) => (
        <section key={section.title} className="section" id={encodeURIComponent(section.title)}>
          <h2 className="section__title">{section.title}</h2>
          <dl className="help__list">
            {section.items.map((item) => (
              <div key={item.id} className="help__item" id={item.id}>
                <dt className="help__q">{item.q}</dt>
                <dd className="help__a">{item.a}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}

      <section className="section">
        <h2 className="section__title">それでも解決しないとき</h2>
        <p className="page__lead">
          お手数ですが、<Link to="/contact">お問い合わせのフォーム</Link>からお知らせください（ログインが必要です）。診断の結果について訊くときは、結果ページの URL を添えていただけると早く調べられます。
        </p>
        <div className="page__actions">
          <Link className="button button--primary" to="/">トップへ戻る</Link>
          {user && <Link className="button button--ghost" to="/me">自分のページ</Link>}
          <Link className="button button--ghost" to="/help/jung">ユングのタイプ論について</Link>
          <Link className="button button--ghost" to="/help/bigfive">ビッグファイブについて</Link>
          <Link className="button button--ghost" to="/terms">利用規約</Link>
          <Link className="button button--ghost" to="/privacy">プライバシーポリシー</Link>
          <Link className="button button--ghost" to="/contact">お問い合わせ</Link>
        </div>
      </section>

      <AdSlot placement="help" />
    </main>
  );
}
